/**
 * Campaign Performance — the Analytics view.
 *
 * Reads the SAME aggregated rows the table renders (the report is fetched
 * whole, bounded by the filter drawer), so switching tabs never re-queries
 * and the two views can never disagree. Every cross-campaign panel —
 * leaderboard, volume mix, assignment, merge status, transfers — is built from
 * those rows alone. Mirrors TeamPerformanceAnalytics re-keyed from team to
 * campaign; see that file for the fuller rationale.
 *
 * The aggregated rows carry no timestamps, so the volume-by-weekday×hour
 * heatmap reads `timelineRows` instead: a separate, lazily-fetched set of raw
 * per-conversation rows scoped identically to the aggregate query (see
 * useCampaignPerformanceTimeline). It renders its own loading state and
 * degrades to an empty-state message if the fetch hasn't completed yet, rather
 * than blocking the rest of the view on it.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  Activity, AlertTriangle, ArrowLeftRight, CalendarClock, GitMerge, ListOrdered,
  Loader2, Megaphone, TrendingUp,
} from 'lucide-react'

import type { CampaignPerformanceRow, CampaignTimelineRow } from '../../../hooks/useCampaignPerformanceReport.js'
import {
  AnalyticsCanvas,
  AttentionList,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  RankTable,
  SectionLabel,
  StatTile,
  type AttentionItem,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  formatCount,
  formatPct,
  hourDayMatrix,
  ratePct,
  sumBy,
} from '../analytics/viz.js'

/** One campaign's totals — the backend already returns one row per campaign. */
interface CampaignAgg {
  key: string
  name: string
  total: number
  newCount: number
  assigned: number
  unassigned: number
  resolved: number
  reOpened: number
  transferred: number
  primary: number
  mergedAsSecondary: number
}

/** How many campaigns a per-campaign chart shows before the tail is dropped. */
const CHART_CAMPAIGNS = 8
/** Unassigned share that reads as a routing problem rather than noise. */
const UNASSIGNED_TARGET = 30
/** Re-open share that reads as a quality problem rather than noise. */
const REOPEN_TARGET = 10

/**
 * Map report rows to the analytics shape. When the report is bucketed by an
 * interval finer than a day, a campaign appears once per bucket — the
 * analytics view sums those buckets back to one figure per campaign.
 */
function toCampaignAggs(rows: readonly CampaignPerformanceRow[]): CampaignAgg[] {
  const byCampaign = new Map<string, CampaignAgg>()
  for (const r of rows) {
    const existing = byCampaign.get(r.campaignId)
    if (existing) {
      existing.total += r.total
      existing.newCount += r.newCount
      existing.assigned += r.assigned
      existing.unassigned += r.unassigned
      existing.resolved += r.resolved
      existing.reOpened += r.reOpened
      existing.transferred += r.transferred
      existing.primary += r.primary
      existing.mergedAsSecondary += r.mergedAsSecondary
    } else {
      byCampaign.set(r.campaignId, {
        key: r.campaignId,
        name: r.campaignName,
        total: r.total,
        newCount: r.newCount,
        assigned: r.assigned,
        unassigned: r.unassigned,
        resolved: r.resolved,
        reOpened: r.reOpened,
        transferred: r.transferred,
        primary: r.primary,
        mergedAsSecondary: r.mergedAsSecondary,
      })
    }
  }
  return [...byCampaign.values()]
}

export default function CampaignPerformanceAnalytics({
  rows,
  timelineRows,
  timelineLoading,
  timelineTruncated,
}: {
  rows: readonly CampaignPerformanceRow[]
  /** Raw per-conversation rows for the heatmap; see file header. */
  timelineRows: readonly CampaignTimelineRow[]
  timelineLoading: boolean
  timelineTruncated: boolean
}): JSX.Element {
  const campaigns = useMemo(() => toCampaignAggs(rows), [rows])

  const totals = useMemo(() => {
    const total = sumBy(campaigns, (c) => c.total)
    return {
      total,
      newCount: sumBy(campaigns, (c) => c.newCount),
      assigned: sumBy(campaigns, (c) => c.assigned),
      unassigned: sumBy(campaigns, (c) => c.unassigned),
      resolved: sumBy(campaigns, (c) => c.resolved),
      reOpened: sumBy(campaigns, (c) => c.reOpened),
      transferred: sumBy(campaigns, (c) => c.transferred),
      primary: sumBy(campaigns, (c) => c.primary),
      mergedAsSecondary: sumBy(campaigns, (c) => c.mergedAsSecondary),
      assignedRate: ratePct(sumBy(campaigns, (c) => c.assigned), total),
      unassignedRate: ratePct(sumBy(campaigns, (c) => c.unassigned), total),
      resolvedRate: ratePct(sumBy(campaigns, (c) => c.resolved), total),
      reopenRate: ratePct(sumBy(campaigns, (c) => c.reOpened), total),
      activeCampaigns: campaigns.filter((c) => c.total > 0).length,
    }
  }, [campaigns])

  const byVolume = useMemo(
    () => [...campaigns].sort((a, b) => b.total - a.total),
    [campaigns],
  )
  const maxTotal = byVolume[0]?.total ?? 0

  const topForCharts = useMemo(
    () => byVolume.filter((c) => c.total > 0).slice(0, CHART_CAMPAIGNS),
    [byVolume],
  )

  /** Volume spread across the roster — the sparkline behind the hero figure. */
  const volumeShape = useMemo(() => byVolume.map((c) => c.total).slice(0, 24), [byVolume])

  // ── Total vs resolved ──────────────────────────────────────────────────────
  const handledData = useMemo(
    () => ({
      labels: topForCharts.map((c) => c.name),
      datasets: [
        barDataset('Total', topForCharts.map((c) => c.total), VIZ.series[0]),
        barDataset('Resolved', topForCharts.map((c) => c.resolved), VIZ.series[2]),
      ],
    }),
    [topForCharts],
  )

  // ── Outcome mix ─────────────────────────────────────────────────────────────
  const mixData = useMemo(
    () => ({
      labels: topForCharts.map((c) => c.name),
      datasets: [
        barDataset('New', topForCharts.map((c) => c.newCount), VIZ.series[0], { stacked: true }),
        barDataset('Assigned', topForCharts.map((c) => c.assigned), VIZ.series[2], { stacked: true }),
        barDataset('Unassigned', topForCharts.map((c) => c.unassigned), VIZ.series[1], { stacked: true }),
        barDataset('Re-opened', topForCharts.map((c) => c.reOpened), VIZ.status.warning, { stacked: true }),
      ],
    }),
    [topForCharts],
  )

  // ── Transfers by campaign ───────────────────────────────────────────────────
  const transferCampaigns = useMemo(
    () => byVolume.filter((c) => c.transferred > 0).slice(0, CHART_CAMPAIGNS),
    [byVolume],
  )
  const transferData = useMemo(
    () => ({
      labels: transferCampaigns.map((c) => c.name),
      datasets: [
        barDataset('Transferred', transferCampaigns.map((c) => c.transferred), VIZ.series[0]),
      ],
    }),
    [transferCampaigns],
  )

  // ── Volume share ────────────────────────────────────────────────────────────
  const shareItems = useMemo(
    (): BarListItem[] =>
      byVolume
        .filter((c) => c.total > 0)
        .map((c) => ({
          label: c.name,
          value: c.total,
          meta: formatPct(ratePct(c.total, totals.total), 1),
        })),
    [byVolume, totals.total],
  )

  // ── Volume by weekday × hour ────────────────────────────────────────────────
  // Needs individual timestamps — drawn from timelineRows, not the aggregate.
  const openedMatrix = useMemo(
    () => hourDayMatrix(timelineRows as unknown as Record<string, unknown>[], 'openedAt'),
    [timelineRows],
  )

  // ── Needs attention ────────────────────────────────────────────────────────
  const attention = useMemo((): AttentionItem[] => {
    const items: AttentionItem[] = []
    for (const campaign of campaigns) {
      if (campaign.total === 0) continue
      const unassignedRate = ratePct(campaign.unassigned, campaign.total)
      const reopenRate = ratePct(campaign.reOpened, campaign.total)

      if (unassignedRate !== null && unassignedRate > UNASSIGNED_TARGET) {
        items.push({
          id: `${campaign.key}-unassigned`,
          title: campaign.name,
          detail: `${formatCount(campaign.unassigned)} unassigned of ${formatCount(campaign.total)}`,
          value: formatPct(unassignedRate, 1),
          tone: unassignedRate > 50 ? 'critical' : 'warning',
        })
      } else if (reopenRate !== null && reopenRate > REOPEN_TARGET) {
        items.push({
          id: `${campaign.key}-reopen`,
          title: campaign.name,
          detail: `${formatCount(campaign.reOpened)} re-opened of ${formatCount(campaign.total)}`,
          value: formatPct(reopenRate, 1),
          tone: 'warning',
        })
      }
    }
    // Worst first — the panel is a work queue, not a directory.
    return items.sort((a, b) => parseFloat(a.value) - parseFloat(b.value)).slice(0, 8)
  }, [campaigns])

  if (campaigns.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no campaign activity. Widen the window or select more
            campaigns, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const topCampaign = byVolume[0]

  return (
    <AnalyticsCanvas>
      {/* ── The one number the view leads with ── */}
      <HeroBand
        eyebrow="Conversations in scope"
        value={formatCount(totals.total)}
        caption={`Across ${totals.activeCampaigns} campaign${totals.activeCampaigns === 1 ? '' : 's'} in the selected window`}
        spark={volumeShape}
        Icon={Megaphone}
        facts={[
          { label: 'Assigned', value: formatCount(totals.assigned) },
          {
            label: 'Assigned rate',
            value: formatPct(totals.assignedRate, 1),
            tone: assignedTone(totals.assignedRate),
          },
          { label: 'Resolved', value: formatCount(totals.resolved) },
          {
            label: 'Re-opened',
            value: formatCount(totals.reOpened),
            tone: reopenTone(totals.reopenRate),
          },
        ]}
      />

      {/* ── Supporting numbers ── */}
      <KpiRow>
        <StatTile
          label="New"
          value={formatCount(totals.newCount)}
          hint={`${formatPct(ratePct(totals.newCount, totals.total), 1)} of total`}
        />
        <StatTile
          label="Unassigned"
          value={formatCount(totals.unassigned)}
          hint={`${formatPct(totals.unassignedRate, 1)} of total`}
          tone={unassignedTone(totals.unassignedRate)}
        />
        <StatTile
          label="Resolved"
          value={formatCount(totals.resolved)}
          hint={`${formatPct(totals.resolvedRate, 1)} of total`}
        />
        <StatTile
          label="Transferred"
          value={formatCount(totals.transferred)}
          hint={`${formatPct(ratePct(totals.transferred, totals.total), 1)} of total`}
        />
        <StatTile
          label="Primary"
          value={formatCount(totals.primary)}
          hint="Not merged into another"
        />
        <StatTile
          label="Merged as secondary"
          value={formatCount(totals.mergedAsSecondary)}
          {...(topCampaign ? { hint: `Busiest: ${topCampaign.name}` } : {})}
        />
      </KpiRow>

      {/* ── Leaderboard ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'} in scope`}>
          Leaderboard
        </SectionLabel>
        <ChartCard
          title="Every campaign, ranked by volume"
          subtitle="Bar length shows each campaign's share of the busiest campaign's load"
          Icon={ListOrdered}
          badge="Scroll for more"
          height={340}
          wide
        >
          <RankTable
            rows={byVolume}
            keyOf={(c) => c.key}
            nameOf={(c) => c.name}
            shareOf={(c) => (maxTotal > 0 ? c.total / maxTotal : 0)}
            columns={[
              { label: 'Total', align: 'right', render: (c) => formatCount(c.total) },
              { label: 'Assigned', align: 'right', render: (c) => formatCount(c.assigned) },
              { label: 'Unassigned', align: 'right', render: (c) => formatCount(c.unassigned) },
              { label: 'Resolved', align: 'right', render: (c) => formatCount(c.resolved) },
              { label: 'Re-opened', align: 'right', render: (c) => formatCount(c.reOpened) },
              { label: 'Transferred', align: 'right', render: (c) => formatCount(c.transferred) },
              {
                label: 'Assigned rate',
                align: 'right',
                render: (c) => formatPct(ratePct(c.assigned, c.total), 1),
              },
            ]}
          />
        </ChartCard>
      </section>

      {/* ── Volume ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Volume &amp; throughput</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Total vs resolved"
            subtitle={`Top ${Math.min(CHART_CAMPAIGNS, topForCharts.length)} campaigns by volume`}
            Icon={TrendingUp}
            footnote={
              totals.resolvedRate !== null
                ? `Campaigns resolved ${formatPct(totals.resolvedRate, 1)} of total volume in this window.`
                : undefined
            }
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations in this window." />
            ) : (
              <Chart
                type="bar"
                data={handledData}
                options={barOptions({ horizontal: true, legend: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Outcome mix"
            subtitle="New, assigned, unassigned and re-opened, per campaign"
            Icon={Activity}
            footnote={`${formatCount(totals.unassigned)} conversations are still unassigned across campaigns.`}
          >
            {topForCharts.length === 0 ? (
              <CardEmpty message="No conversations in this window." />
            ) : (
              <Chart
                type="bar"
                data={mixData}
                options={barOptions({ horizontal: true, legend: true, stacked: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Assignment & merge status ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Assignment &amp; merge status</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Assigned vs unassigned"
            subtitle="Every conversation in scope, by assignment state"
            height={220}
          >
            <Meter
              segments={[
                { label: 'Assigned', value: totals.assigned, color: VIZ.series[0] },
                { label: 'Unassigned', value: totals.unassigned, color: VIZ.series[1] },
              ]}
              emptyMessage="No conversations in this window."
            />
          </ChartCard>
          <ChartCard
            title="Primary vs merged as secondary"
            subtitle="Conversations that survived a merge vs. those folded into another"
            Icon={GitMerge}
            height={220}
          >
            <Meter
              segments={[
                { label: 'Primary', value: totals.primary, color: VIZ.series[2] },
                { label: 'Merged as secondary', value: totals.mergedAsSecondary, color: VIZ.series[1] },
              ]}
              emptyMessage="No conversations in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Load concentration ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${formatCount(totals.total)} across ${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}>
          Load concentration
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Share of total volume"
            subtitle="Each campaign's slice of everything in this window"
            Icon={Megaphone}
            height={320}
          >
            <BarList
              items={shareItems}
              format={formatCount}
              emptyMessage="No conversations in this window."
            />
          </ChartCard>

          <ChartCard
            title="Transferred, by campaign"
            subtitle="Where handoffs are originating"
            Icon={ArrowLeftRight}
          >
            {transferCampaigns.length === 0 ? (
              <CardEmpty message="No transfers in this window." />
            ) : (
              <Chart
                type="bar"
                data={transferData}
                options={barOptions({ horizontal: true, legend: false })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Attention ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Needs attention</SectionLabel>
        <ChartCard
          title="Campaigns to look at"
          subtitle={`More than ${UNASSIGNED_TARGET}% unassigned, or re-opening more than ${REOPEN_TARGET}%`}
          Icon={AlertTriangle}
          height={300}
        >
          <AttentionList
            items={attention}
            emptyMessage={`Every campaign is within the ${UNASSIGNED_TARGET}% unassigned and ${REOPEN_TARGET}% re-open thresholds.`}
          />
        </ChartCard>
      </section>

      {/* ── Timing ── */}
      {/* Sourced from timelineRows (per-conversation), not the aggregate rows —
          see file header. Loads separately, so it never blocks the rest of the
          view; a quiet spinner and an empty state cover the in-between. */}
      <section className="flex flex-col gap-3">
        <SectionLabel
          {...(timelineTruncated
            ? { note: 'Capped at the first 20,000 conversations in this window' }
            : {})}
        >
          Timing
        </SectionLabel>
        <ChartCard
          title="When conversations arrive"
          subtitle="Volume by day of week and hour"
          Icon={CalendarClock}
          wide
        >
          {timelineLoading ? (
            <div className="flex h-full items-center justify-center gap-2 text-[12px] text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading timing data…
            </div>
          ) : openedMatrix.total === 0 ? (
            <CardEmpty message="No conversation timestamps in this window." />
          ) : (
            <Heatmap matrix={openedMatrix.matrix} max={openedMatrix.max} />
          )}
        </ChartCard>
      </section>
    </AnalyticsCanvas>
  )
}

/** Assignment tone — higher is better here, so the bands run the other way. */
function assignedTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct >= 80) return 'good'
  if (pct >= 60) return 'warning'
  return 'critical'
}

function unassignedTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 10) return 'good'
  if (pct <= UNASSIGNED_TARGET) return 'warning'
  return 'critical'
}

function reopenTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 5) return 'good'
  if (pct <= REOPEN_TARGET) return 'warning'
  return 'critical'
}
